import React from 'react';
import { format, subDays } from 'date-fns';
import clsx from 'clsx';
import { UptimeStats, UptimeRecord } from '../../types';

interface UptimeChartProps {
  data: UptimeStats | null;
  isLoading: boolean;
}

const UptimeChart: React.FC<UptimeChartProps> = ({ data, isLoading }) => {
  // Generate 90 days of placeholders for the chart
  const days = Array.from({ length: 90 }, (_, i) => {
    const date = subDays(new Date(), 89 - i);
    return {
      date: format(date, 'yyyy-MM-dd'),
      dayLabel: format(date, 'd'),
      monthLabel: format(date, 'MMM'),
      isFirstOfMonth: date.getDate() === 1,
    };
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="flex gap-0.5">
            {Array.from({ length: 90 }).map((_, i) => (
              <div key={i} className="h-8 w-1.5 bg-gray-200 rounded-sm"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Create a map of date to uptime data
  const uptimeMap = new Map<string, UptimeRecord>();
  if (data?.daily) {
    data.daily.forEach((record) => {
      uptimeMap.set(record.date, record);
    });
  }

  const getBarColor = (percentage: number | undefined): string => {
    if (percentage === undefined) return 'bg-gray-200'; // No data
    if (percentage >= 99.9) return 'bg-green-500';
    if (percentage >= 99.0) return 'bg-green-400';
    if (percentage >= 95.0) return 'bg-amber-400';
    if (percentage >= 90.0) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getTooltipText = (date: string): string => {
    const record = uptimeMap.get(date);
    if (!record) return `${format(new Date(date), 'MMM d, yyyy')}: No data`;
    return `${format(new Date(date), 'MMM d, yyyy')}: ${record.uptimePercentage.toFixed(2)}% uptime`;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">90-Day Uptime</h2>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-gray-900">
              {data?.last90Days?.toFixed(2) ?? '--'}%
            </span>
          </div>
        </div>
      </div>

      {/* Uptime bars */}
      <div className="relative">
        <div className="flex gap-0.5">
          {days.map((day) => {
            const record = uptimeMap.get(day.date);
            return (
              <div
                key={day.date}
                className="group relative flex-1 min-w-0"
              >
                <div
                  className={clsx(
                    'h-8 rounded-sm cursor-pointer transition-opacity hover:opacity-80',
                    getBarColor(record?.uptimePercentage)
                  )}
                  title={getTooltipText(day.date)}
                />
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                  {getTooltipText(day.date)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Date labels */}
        <div className="flex mt-2 text-xs text-gray-400">
          <span>90 days ago</span>
          <span className="ml-auto">Today</span>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-6 pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded-sm"></div>
          <span className="text-xs text-gray-500">&gt;99.9%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-400 rounded-sm"></div>
          <span className="text-xs text-gray-500">99-99.9%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-amber-400 rounded-sm"></div>
          <span className="text-xs text-gray-500">95-99%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-red-500 rounded-sm"></div>
          <span className="text-xs text-gray-500">&lt;95%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-gray-200 rounded-sm"></div>
          <span className="text-xs text-gray-500">No data</span>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-gray-100">
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-900">
            {data?.last30Days?.toFixed(2) ?? '--'}%
          </p>
          <p className="text-xs text-gray-500">Last 30 days</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-900">
            {data?.last90Days?.toFixed(2) ?? '--'}%
          </p>
          <p className="text-xs text-gray-500">Last 90 days</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-900">
            {data?.lastYear?.toFixed(2) ?? '--'}%
          </p>
          <p className="text-xs text-gray-500">Last 12 months</p>
        </div>
      </div>
    </div>
  );
};

export default UptimeChart;
